import { useCallback, useEffect, useState } from 'react';

export const ANNIVERSARY_BANNER_DISMISSED_KEY = 'anniversaryBannerDismissed';

// Live from 29 Sep 2026 to 30 Sep 2027 inclusive, NZ time (NZDT, UTC+13 on both dates)
export const ANNIVERSARY_BANNER_START = new Date('2026-09-29T00:00:00+13:00');
export const ANNIVERSARY_BANNER_END = new Date('2027-10-01T00:00:00+13:00');

// setTimeout overflows (and fires immediately) above this, so longer waits are done in steps
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

export const isAnniversaryBannerLive = (now: Date) =>
  now >= ANNIVERSARY_BANNER_START && now < ANNIVERSARY_BANNER_END;

const getNextBoundary = (now: Date) => {
  if (now < ANNIVERSARY_BANNER_START) {
    return ANNIVERSARY_BANNER_START;
  }
  if (now < ANNIVERSARY_BANNER_END) {
    return ANNIVERSARY_BANNER_END;
  }
  return null;
};

const readDismissed = () => {
  try {
    return localStorage.getItem(ANNIVERSARY_BANNER_DISMISSED_KEY) === 'true';
  } catch {
    // Storage blocked (e.g. privacy mode) - still show the banner, it just won't stay dismissed
    return false;
  }
};

/**
 * Visibility of the 20-year anniversary banner. Resolved after mount because the dismissed flag
 * lives in localStorage, which isn't available during SSR. Re-evaluated at the campaign start and
 * end so a tab left open across either boundary updates without a reload.
 */
export const useAnniversaryBanner = () => {
  const [isLive, setIsLive] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    setIsDismissed(readDismissed());

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const evaluate = () => {
      const now = new Date();
      setIsLive(isAnniversaryBannerLive(now));

      const nextBoundary = getNextBoundary(now);
      if (nextBoundary) {
        const delay = Math.min(nextBoundary.getTime() - now.getTime(), MAX_TIMEOUT_MS);
        timeoutId = setTimeout(evaluate, delay);
      }
    };
    evaluate();

    return () => clearTimeout(timeoutId);
  }, []);

  const dismiss = useCallback(() => {
    // Kept in state too, so a later re-evaluation can't resurface it when storage is blocked
    setIsDismissed(true);
    try {
      localStorage.setItem(ANNIVERSARY_BANNER_DISMISSED_KEY, 'true');
    } catch {
      // Storage blocked - banner is hidden for this page view only
    }
  }, []);

  return { isVisible: isLive && !isDismissed, dismiss };
};
